import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Ruler, X, Trash2 } from 'lucide-react';
import { useSizeEditor } from '@/hooks/useSizeEditor';

export default function SizesBreakout({ opening, onClose }) {
  const state = useSizeEditor(opening);
  const { controller, rows, active, pending, unknown, loading, saving, review, reviewNeeded } = state;
  const currentUsed = new Set((state.baseline || []).filter(row => row.in_use).map(row => row.label));
  const reason = controller.blockedReason();
  const renderRow = row => {
    const protectedRow = row.originLabel === row.label && currentUsed.has(row.label);
    return <div key={row.id} data-size-row={row.id} className="flex items-center gap-2">
      {protectedRow || row.archived ? <span className={`flex-1 text-sm ${row.archived ? 'text-stone-400 line-through' : 'text-stone-700'}`}>{row.label}</span>
        : <Input value={row.label} onChange={event => controller.update(row.id, { label: event.target.value })}
          placeholder="Size name" aria-label="Size name" className="flex-1 h-8 text-sm" />}
      {protectedRow && <span className="text-[10px] text-stone-400 uppercase">in use</span>}
      {!protectedRow && <button title="Delete" aria-label={`Delete ${row.label || 'unnamed size'}`} onClick={() => controller.remove(row.id)}><Trash2 className="h-4 w-4" /></button>}
      {(row.archived || protectedRow) && <button className="text-xs text-primary" onClick={() => controller.update(row.id, { archived: !row.archived })}>
        {row.archived ? 'Restore' : 'Archive'}
      </button>}
    </div>;
  };
  return <Card data-testid="sizes-breakout">
    <CardHeader className="flex flex-row items-center justify-between space-y-0">
      <CardTitle className="flex items-center gap-2 text-base"><Ruler className="h-5 w-5" /> Edit Sizes</CardTitle>
      <button onClick={onClose} data-testid="close-sizes-breakout" aria-label="Close"><X className="h-4 w-4" /></button>
    </CardHeader>
    <CardContent className="space-y-4">
      {!active ? <p role="status">This size editor no longer belongs to the active cloud administrator. Reopen Edit Sizes.</p> : <>
        <p className="text-xs text-stone-500">A size in use can be archived (hidden from menus) but not renamed or deleted, so past orders keep their exact size.</p>
        {state.notice && <p role="status" className="text-sm">{state.notice}</p>}
        {pending && <p role="status">A size replacement is still pending. Closing or reopening this editor does not cancel it.</p>}
        {unknown && <p role="status">An earlier replacement has an unknown outcome. A current read cannot prove which request saved it.</p>}
        {loading && <p role="status">Checking current sizes…</p>}
        {state.readError && <p role="alert">{state.readError}</p>}
        {state.choicesError && <p role="alert">The shared size choices are not confirmed current. Refresh sizes without repeating the save; this does not change the reported save outcome.</p>}
        {rows && <>
          <div className="space-y-2">{rows.filter(row => !row.archived).map(renderRow)}</div>
          <button onClick={() => controller.add()} data-testid="add-size-btn" className="text-xs text-primary">+ Add size</button>
          {rows.some(row => row.archived) && <div className="space-y-2 border-t pt-2"><p className="text-xs uppercase">Archived</p>{rows.filter(row => row.archived).map(renderRow)}</div>}
        </>}
        {review && <div className="space-y-2 border rounded p-3" data-testid="size-current-review">
          <p className="text-sm">Current server sizes are shown separately. Review these constraints before replacing the whole list. Your draft has not been merged or overwritten.</p>
          <ul>{review.map(row => <li key={row.label}>{row.label} — {row.archived ? 'archived' : 'active'}{row.in_use ? ', in use' : ''}</li>)}</ul>
          <Button variant="outline" disabled={loading || saving || pending} onClick={() => controller.review()}>Use current size constraints</Button>
        </div>}
        {reviewNeeded && !review && !loading && rows && <p className="text-sm">Refresh current sizes before reviewing another replacement.</p>}
        {reason && rows && !saving && !loading && !pending && <p className="text-xs text-stone-500">{reason}</p>}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="outline" disabled={pending || loading || saving} onClick={() => controller.refresh()}>{rows ? 'Refresh sizes' : 'Retry size list'}</Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => controller.save()} disabled={!!reason} data-testid="save-sizes-btn">{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </>}
    </CardContent>
  </Card>;
}
